import {
  Box,
  Button,
  Center,
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spacer,
  Text,
  useBreakpointValue,
  useDisclosure,
  useToken,
} from "@chakra-ui/react";
import React, { ReactNode, useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import nativeTabBar, {
  isNativeTabBarAvailable,
  type NativeTabItem,
} from "../../../plugins/nativeTabBar";
import { useMemberReviewStatus } from "../../../hooks/useItems";
import { useAuth } from "../../../hooks/useAuth";
import { useHideTabBar } from "../../../hooks/useHideTabBar";
import { useTabBarStore } from "../../../stores/tabBarStore";
import ProfileModal from "../../react/Settings/Settings";
import NotificationBellButton from "../drawers/NotificationDrawer";
import PluginDrawer from "../drawers/PluginDrawer";
import NewVersionModal from "../modals/NewVersionModal";
import VersionBadge from "../badges/VersionBadge";
import {
  Ticket,
  LayoutGrid,
  BarChart3,
  Package,
  Workflow,
  Search,
  ClipboardList,
  SearchCheck,
  Layout,
  Globe,
  Store,
  Building,
  Users,
  Clock,
  UserCircle,
  Sparkles,
  SlidersHorizontal,
  ShieldCheck,
  Fingerprint,
  ArrowLeftRight,
  HelpCircle,
  Settings,
  PanelLeftClose,
  ChevronLeft,
  MoreVertical,
  Zap,
  ChevronDown,
  ChevronRight,
  LogOut,
  MoreHorizontal,
  BellDot,
} from "lucide-react";
import LazyLoadAvatar from "../media/LazyLoadedAvatar";
import ClockInClockOut from "../ClockInClockOut";
import FaqModal from "../modals/FaqModal";
import FloatingNotifications from "../FloatingNotifications";
import OceanProSidebarOverlay from "../OceanProSidebarOverlay";
import KrakenGlobalFab from "../../react/Kraken/KrakenAIChat/KrakenGlobalFab";
import { KrakenProvider } from "../../react/Kraken/KrakenAIChat/KrakenContext";
import {
  getAppSideNavCollapsed,
  getSidebarCollapsed,
  setAppSideNavCollapsed,
  setSidebarCollapsed,
} from "../../../utils/functions/sidebarTracking";

const hideScrollbarStyle = {
  "&::-webkit-scrollbar": {
    display: "none",
  },
  msOverflowStyle: "none",
  scrollbarWidth: "none",
};

const AppButton = ({
  label,
  icon,
  active,
  href,
}: {
  label: string;
  icon: string;
  active?: boolean;
  href?: string;
}) => {
  const interactive = !!href;
  const button = (
    <Center
      aria-label={label}
      title={label}
      w="44px"
      h="44px"
      p="2px"
      cursor={interactive ? "pointer" : "default"}
      borderRadius="11px"
      position="relative"
      transition="transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 220ms ease-out, background 220ms ease-out"
      sx={{
        background: active
          ? "linear-gradient(135deg, var(--chakra-colors-primary-500) 0%, var(--chakra-colors-primary-400) 50%, var(--chakra-colors-primary-300) 100%)"
          : "linear-gradient(135deg, rgba(15,23,42,0.10) 0%, rgba(15,23,42,0.06) 100%)",
        boxShadow: active
          ? "0 6px 16px -8px rgba(54, 46, 173, 0.55), 0 1px 0 0 rgba(255,255,255,0.6) inset, 0 -1px 0 0 rgba(15,23,42,0.08) inset"
          : "0 1px 0 0 rgba(255,255,255,0.7) inset",
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      }}
      _hover={
        interactive
          ? {
              transform: "translateY(-1px) scale(1.03)",
              sx: {
                background:
                  "linear-gradient(135deg, var(--chakra-colors-primary-400) 0%, var(--chakra-colors-primary-300) 100%)",
                boxShadow:
                  "0 18px 40px -10px rgba(54, 46, 173, 0.65), 0 8px 18px -6px rgba(54, 46, 173, 0.4), 0 1px 0 0 rgba(255,255,255,0.7) inset",
              },
            }
          : undefined
      }
      _active={
        interactive ? { transform: "translateY(0) scale(0.96)" } : undefined
      }
    >
      {active && (
        <Box
          position="absolute"
          inset="-3px"
          borderRadius="14px"
          pointerEvents="none"
          sx={{
            background:
              "radial-gradient(closest-side, rgba(124, 109, 242, 0.35), transparent 70%)",
            filter: "blur(2px)",
            zIndex: -1,
          }}
        />
      )}
      <Center
        w="100%"
        h="100%"
        bg="white"
        borderRadius="9px"
        overflow="hidden"
        boxShadow="0 1px 2px rgba(15,23,42,0.06), 0 0 0 1px rgba(15,23,42,0.04)"
      >
        <Box
          w="30px"
          h="30px"
          bgImage={`url('${icon}')`}
          bgPos="center"
          bgSize="contain"
          bgRepeat="no-repeat"
          sx={{
            filter: active
              ? "drop-shadow(0 1px 1px rgba(15,23,42,0.08))"
              : "none",
          }}
        />
      </Center>
    </Center>
  );

  if (!href) return button;

  return (
    <Box as="a" href={href} aria-label={label} title={label}>
      {button}
    </Box>
  );
};

const AppSideNav = ({
  isOpen,
  onToggle,
  isReact,
}: {
  isOpen: boolean;
  onToggle: () => void;
  isReact: boolean;
}) => {
  return (
    <Box
      w={isOpen ? "72px" : "0px"}
      minW={isOpen ? "72px" : "0px"}
      h="100vh"
      ml="-8px"
      mt="-8px"
      mb="-8px"
      overflow="visible"
      transition="width 0.2s ease-out, min-width 0.2s ease-out"
      position="relative"
      flexShrink={0}
    >
      <Box w="100%" h="100%" overflow="hidden">
        <Box
          w="72px"
          h="100%"
          bg="white"
          borderRadius="0"
          boxShadow="12px 0 28px -20px rgba(15, 23, 42, 0.55), inset -1px 0 0 rgba(15, 23, 42, 0.08)"
          overflow="hidden"
          position="relative"
        >
          <Flex
            position="relative"
            zIndex={1}
            direction="column"
            align="center"
            gap="10px"
            mt="32px"
          >
            <AppButton label="Ocean" icon="/logo/blue-md.svg" active />
            <AppButton
              label="React Portal"
              icon="/logo/react.svg"
              href={import.meta.env.VITE_PORTAL_URL}
            />
            {isReact && (
              <>
                <AppButton
                  label="Outlook"
                  icon="/logo/outlook.svg"
                  href="https://outlook.office.com/"
                />
                <AppButton
                  label="Teams"
                  icon="/logo/teams.svg"
                  href="https://teams.microsoft.com/"
                />
                <AppButton
                  label="SharePoint"
                  icon="/logo/sharepoint.svg"
                  href="https://www.office.com/launch/sharepoint"
                />
                <AppButton
                  label="About Work"
                  icon="/logo/aboutwork.svg"
                  href="https://portal.aboutwrk.com/user/home"
                />
              </>
            )}
          </Flex>
        </Box>
      </Box>

      <IconButton
        aria-label={isOpen ? "Collapse apps" : "Expand apps"}
        variant="unstyled"
        size="xs"
        position="absolute"
        top="50%"
        transform="translateY(-50%)"
        right="-12px"
        w="24px"
        h="24px"
        minW="24px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="white"
        border="1px solid"
        borderColor={isOpen ? "primary.300" : "primary.500"}
        borderRadius="full"
        boxShadow="sm"
        zIndex={100}
        color="neutral.500"
        _hover={{
          bg: "primary.500",
          borderColor: "neutral.500",
          color: "white",
        }}
        onClick={onToggle}
        icon={
          <Box
            transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
            transition="transform 0.2s ease-out"
            ml={isOpen ? "0" : "8px"}
          >
            <ChevronRight size={14} />
          </Box>
        }
      />
    </Box>
  );
};

const NavButton = (props: {
  label: string;
  path: string;
  activeRoute?: string;
  icon: ReactNode;
  openModal?: () => void;
  isLink: boolean;
  isCollapsed: boolean;
  count?: number;
  hasExclamation?: boolean;
  isProFeature?: boolean;
  isInDrawer?: boolean;
  onNavigate?: () => void;
}) => {
  const location = useLocation();
  const { permissions } = useAuth();
  const isProMode = permissions.isSuperAdmin;

  const path = props.activeRoute ?? props.path;

  const isActive =
    location.pathname === path ||
    location.pathname === `${path}/` ||
    (location.pathname.includes(path) && path !== "/react/admin");

  const activeBg = isProMode
    ? "rgba(124, 58, 237, 0.3)"
    : "rgba(54, 46, 173, 0.2)";
  const hoverBg = isProMode
    ? "rgba(124, 58, 237, 0.15)"
    : "rgba(54, 46, 173, 0.1)";

  const box = (
    <Flex
      position="relative"
      alignItems="center"
      justifyContent={props.isCollapsed ? "center" : "flex-start"}
      gap={
        props.isCollapsed
          ? "0"
          : props.isInDrawer
            ? "14px"
            : { base: "8px", xl: "10px" }
      }
      p={
        props.isCollapsed ? "8px" : props.isInDrawer ? "10px 12px" : "8px 10px"
      }
      minH={props.isInDrawer ? "44px" : "36px"}
      h={props.isInDrawer ? "auto" : "36px"}
      w="100%"
      borderRadius="10px"
      bg={isActive ? activeBg : "transparent"}
      mb="2px"
      transition="all 0.2s ease-out"
      _hover={{
        bg: isActive ? activeBg : hoverBg,
      }}
      color={isActive ? "white" : "secondary.200"}
    >
      {props.isInDrawer && isActive && (
        <Box
          position="absolute"
          left="-6px"
          top="50%"
          transform="translateY(-50%)"
          w="3px"
          h="20px"
          borderRadius="2px"
          bg={isProMode ? "white" : "secondary.100"}
        />
      )}
      <Box
        flexShrink={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        color="inherit"
        transition="color 0.2s ease-out"
      >
        {props.icon}
      </Box>
      <Text
        textStyle={props.isInDrawer ? "b-md" : { base: "b-xs", xl: "b-sm" }}
        color="inherit"
        fontWeight={isActive ? "500" : "400"}
        flex={props.isInDrawer ? "1" : undefined}
        w={
          props.isCollapsed
            ? "0"
            : props.isInDrawer
              ? "auto"
              : { base: "80px", xl: "120px" }
        }
        noOfLines={1}
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        transition="all 0.2s ease-out"
        opacity={props.isCollapsed ? 0 : 1}
      >
        {props.label}
      </Text>
      {(props.count || props.hasExclamation) && !props.isCollapsed && (
        <Center p="4px 8px" bg="red.500" borderRadius="32px" ml="auto">
          <Text textStyle="b-xs" fontWeight="510" color="white">
            {props.hasExclamation ? "!" : props.count}
          </Text>
        </Center>
      )}
      {props.isProFeature && !props.isCollapsed && (
        <Box
          ml="auto"
          flexShrink={0}
          px="5px"
          py="4px"
          borderRadius="5px"
          boxShadow="0 0 6px rgba(124,58,237,0.45)"
          sx={{
            "@keyframes pro-shimmer": {
              "0%": { backgroundPosition: "0% 0%" },
              "100%": { backgroundPosition: "100% 100%" },
            },
            background:
              "linear-gradient(135deg, #5b21b6 0%, #7c3aed 40%, #a78bfa 50%, #7c3aed 60%, #5b21b6 100%)",
            backgroundSize: "300% 300%",
            animation: "pro-shimmer 2.4s linear infinite",
          }}
        >
          <Text
            fontSize="8.5px"
            fontWeight="700"
            letterSpacing="0.08em"
            color="white"
            lineHeight="1"
          >
            PRO
          </Text>
        </Box>
      )}
    </Flex>
  );

  return (
    <>
      {props.isLink ? (
        <Link to={props.path} onClick={props.onNavigate}>
          {box}
        </Link>
      ) : (
        <Box
          onClick={() => {
            props.openModal?.();
            props.onNavigate?.();
          }}
          cursor="pointer"
        >
          {box}
        </Box>
      )}
    </>
  );
};

const NavSection = ({
  label,
  isCollapsed,
  isSectionCollapsed,
  onToggle,
  isProHighlighted,
}: {
  label: string;
  isCollapsed: boolean;
  isSectionCollapsed: boolean;
  onToggle: () => void;
  isProHighlighted?: boolean;
}) => {
  return (
    <Box pos="relative" h="30px" cursor="pointer" onClick={onToggle}>
      <Divider
        borderColor={isProHighlighted ? "#7c3aed" : "primary.400"}
        borderWidth="1px"
        pos="absolute"
        top="15px"
        opacity={isCollapsed ? "1" : "0"}
        transition="0.2s ease-out"
      />
      <Flex
        alignItems="center"
        w="full"
        gap="6px"
        top="12px"
        h="14px"
        pos="absolute"
        opacity={isCollapsed ? "0" : "1"}
        transition="0.2s ease-out"
      >
        <Text
          fontSize="10px"
          letterSpacing="0.08em"
          textTransform="uppercase"
          color={isProHighlighted ? "#c4b5fd" : "secondary.200"}
          fontWeight="600"
          opacity="0.7"
        >
          {label}
        </Text>
        <Spacer />
        <Box
          color={isProHighlighted ? "#c4b5fd" : "secondary.200"}
          transform={isSectionCollapsed ? "rotate(-90deg)" : "rotate(0deg)"}
          transition="0.2s ease-out"
          opacity="0.5"
        >
          <ChevronDown size={12} />
        </Box>
      </Flex>
    </Box>
  );
};

const SidebarContent = ({
  navCollapsed,
  setNavCollapsed,
  isInDrawer,
  onNavigate,
  permissions,
  user,
  isNative,
  logout,
  logoutNative,
  hasPendingReviews,
  collapsedSections,
  toggleSection,
  onFaqOpen,
  onUserSettingsOpen,
  bgColor,
  isProMode,
  isNotificationDrawerOpen,
  onNotificationDrawerOpen,
  onNotificationDrawerClose,
}: {
  navCollapsed: boolean;
  setNavCollapsed: (v: boolean) => void;
  isInDrawer: boolean;
  onNavigate?: () => void;
  permissions: ReturnType<typeof useAuth>["permissions"];
  user: ReturnType<typeof useAuth>["user"];
  isNative: boolean;
  logout: ReturnType<typeof useAuth>["logout"];
  logoutNative: ReturnType<typeof useAuth>["logoutNative"];
  hasPendingReviews: boolean | undefined;
  collapsedSections: { [key: string]: boolean };
  toggleSection: (name: string) => void;
  onFaqOpen: () => void;
  onUserSettingsOpen: () => void;
  bgColor: string;
  isProMode: boolean;
  isNotificationDrawerOpen: boolean;
  onNotificationDrawerOpen: () => void;
  onNotificationDrawerClose: () => void;
}) => {
  const effectiveCollapsed = isInDrawer ? false : navCollapsed;

  return (
    <>
      {!isInDrawer && (
        <Box
          pos="absolute"
          top="44px"
          w="calc(100% - 33px)"
          h="32px"
          left="14px"
          zIndex={2}
          bg="transparent"
          bgGradient={
            isProMode
              ? "linear(to-b, rgba(30, 27, 75, 1) 0%, rgba(30, 27, 75, 0) 100%)"
              : [
                  `linear(to-b, ${bgColor}, ${bgColor})`,
                  `linear(to-b, ${bgColor} 50.62%, rgba(54, 46, 173, 0) 99.86%)`,
                ]
          }
        />
      )}

      <Flex
        maxH={isInDrawer ? "100px" : "80px"}
        justifyContent={isInDrawer ? "center" : "space-between"}
        alignItems="center"
        gap={isInDrawer ? "12px" : undefined}
        p={
          effectiveCollapsed
            ? "0 2px"
            : isInDrawer
              ? "20px 24px 12px"
              : undefined
        }
        position="relative"
        zIndex={1}
      >
        <Box
          bgImage="/logo/white-sm.svg"
          bgPos="center"
          bgSize="contain"
          bgRepeat="no-repeat"
          w={effectiveCollapsed ? "0" : isInDrawer ? "128px" : "80px"}
          h={isInDrawer ? "44px" : "32px"}
          transition="width 0.2s ease-out"
          flexShrink={0}
        />

        {!isInDrawer && (
          <IconButton
            onClick={() => setNavCollapsed(!navCollapsed)}
            transform={navCollapsed ? "rotate(180deg)" : undefined}
            transition="0.2s ease-out"
            aria-label="Collapse"
            variant="rounded"
            size="xs"
            color="secondary.500"
            icon={<PanelLeftClose size={14} />}
          />
        )}
      </Flex>

      <Box
        h={isInDrawer ? "calc(100% - 200px)" : "calc(100% - 100px)"}
        overflowY="scroll"
        overflowX="hidden"
        pl={isInDrawer ? "16px" : "5px"}
        pr={isInDrawer ? "16px" : "0"}
        pb={isInDrawer ? "16px" : "0"}
        position="absolute"
        left={isInDrawer ? "0" : "14px"}
        w={isInDrawer ? "100%" : "calc(100% - 33px)"}
        sx={{
          ...hideScrollbarStyle,
          ...(isInDrawer && {
            maskImage:
              "linear-gradient(to bottom, black calc(100% - 24px), transparent)",
            WebkitMaskImage:
              "linear-gradient(to bottom, black calc(100% - 24px), transparent)",
          }),
        }}
      >
        <Spacer h="20px" />
        {permissions.isAdmin && (
          <>
            <NavButton
              label="Tickets"
              path="/react/tickets"
              activeRoute="/react/tickets"
              icon={<Ticket size={18} />}
              isLink={true}
              isCollapsed={effectiveCollapsed}
              isInDrawer={isInDrawer}
              onNavigate={onNavigate}
            />
          </>
        )}

        <NavSection
          label="HOME"
          isCollapsed={effectiveCollapsed}
          isSectionCollapsed={collapsedSections.HOME}
          onToggle={() => toggleSection("HOME")}
        />

        {!collapsedSections.HOME && (
          <>
            <NavButton
              label="Dashboard"
              path="/react/dashboard"
              icon={<LayoutGrid size={18} />}
              isLink={true}
              isCollapsed={effectiveCollapsed}
              isInDrawer={isInDrawer}
              onNavigate={onNavigate}
            />
            <NavButton
              label="Reporting"
              path="/react/reporting"
              activeRoute="/react/reporting"
              icon={<BarChart3 size={18} />}
              isLink={true}
              isCollapsed={effectiveCollapsed}
              isProFeature={permissions.isMemberPro || permissions.isSuperAdmin}
              isInDrawer={isInDrawer}
              onNavigate={onNavigate}
            />
          </>
        )}

        <NavSection
          label="WORK"
          isCollapsed={effectiveCollapsed}
          isSectionCollapsed={collapsedSections.WORK}
          onToggle={() => toggleSection("WORK")}
        />

        {!collapsedSections.WORK && (
          <>
            <NavButton
              label="Items"
              path="/react/items/overview"
              activeRoute="/react/items"
              icon={<Package size={18} />}
              isLink={true}
              isCollapsed={effectiveCollapsed}
              isInDrawer={isInDrawer}
              onNavigate={onNavigate}
            />
            {permissions.isAdmin && (
              <NavButton
                label="Pipeline"
                path="/react/pipeline"
                icon={<Workflow size={18} />}
                isLink={true}
                isCollapsed={effectiveCollapsed}
                isInDrawer={isInDrawer}
                onNavigate={onNavigate}
              />
            )}
            {(permissions.isAdmin || permissions.isMember) && (
              <NavButton
                label="Intelligence"
                path="/react/intelligence/list/0"
                activeRoute="/react/intelligence"
                icon={<Search size={18} />}
                isLink={true}
                isCollapsed={effectiveCollapsed}
                isInDrawer={isInDrawer}
                onNavigate={onNavigate}
              />
            )}
            <NavButton
              label="Whitelisting"
              path="/react/whitelist/sellers"
              activeRoute="/react/whitelist"
              icon={<ClipboardList size={18} />}
              isLink={true}
              isCollapsed={effectiveCollapsed}
              isInDrawer={isInDrawer}
              onNavigate={onNavigate}
            />
            {permissions.isMember && (
              <NavButton
                label="Review"
                path="/member/review/platform_items"
                activeRoute="/member/review"
                icon={<SearchCheck size={18} />}
                isLink={true}
                isCollapsed={effectiveCollapsed}
                hasExclamation={hasPendingReviews}
                isInDrawer={isInDrawer}
                onNavigate={onNavigate}
              />
            )}
          </>
        )}

        <NavSection
          label="ENTITIES"
          isCollapsed={effectiveCollapsed}
          isSectionCollapsed={collapsedSections.ENTITIES}
          onToggle={() => toggleSection("ENTITIES")}
        />

        {!collapsedSections.ENTITIES && (
          <>
            <NavButton
              label="Platforms"
              path="/react/platforms/list"
              activeRoute="/react/platforms"
              icon={<Layout size={18} />}
              isLink={true}
              isCollapsed={effectiveCollapsed}
              isInDrawer={isInDrawer}
              onNavigate={onNavigate}
            />
            <NavButton
              label="Sites"
              path="/react/saw"
              icon={<Globe size={18} />}
              isLink={true}
              isCollapsed={effectiveCollapsed}
              isInDrawer={isInDrawer}
              onNavigate={onNavigate}
            />
            <NavButton
              label="Sellers"
              path="/react/sellers/list/platform"
              activeRoute="/react/sellers"
              icon={<Store size={18} />}
              isLink={true}
              isCollapsed={effectiveCollapsed}
              isInDrawer={isInDrawer}
              onNavigate={onNavigate}
            />
          </>
        )}

        {permissions.isMember && (
          <NavButton
            label="My Organization"
            path="/member/organization"
            icon={<Building size={18} />}
            isLink={true}
            isCollapsed={effectiveCollapsed}
            isInDrawer={isInDrawer}
            onNavigate={onNavigate}
          />
        )}

        {permissions.isReact && (
          <NavSection
            label="ADMIN"
            isCollapsed={effectiveCollapsed}
            isSectionCollapsed={collapsedSections.ADMIN}
            onToggle={() => toggleSection("ADMIN")}
          />
        )}
        {permissions.isReact && !collapsedSections.ADMIN && (
          <NavButton
            label="Members"
            path="/react/members/list"
            icon={<Users size={18} />}
            isLink={true}
            isCollapsed={effectiveCollapsed}
            isInDrawer={isInDrawer}
            onNavigate={onNavigate}
          />
        )}
        {permissions.isReact && !collapsedSections.ADMIN && (
          <NavButton
            label="Hour Registrations"
            path="/react/hour-registrations"
            icon={<Clock size={18} />}
            isLink={true}
            isCollapsed={effectiveCollapsed}
            isInDrawer={isInDrawer}
            onNavigate={onNavigate}
          />
        )}
        {permissions.isAdmin && !collapsedSections.ADMIN && (
          <NavButton
            label="Users"
            path="/react/users"
            icon={<UserCircle size={18} />}
            isLink={true}
            isCollapsed={effectiveCollapsed}
            isInDrawer={isInDrawer}
            onNavigate={onNavigate}
          />
        )}
        {permissions.isAdmin && !collapsedSections.ADMIN && (
          <NavButton
            label="AI Classifier"
            path="/react/ai/logo"
            activeRoute="/react/ai"
            icon={<Sparkles size={18} />}
            isLink={true}
            isCollapsed={effectiveCollapsed}
            isInDrawer={isInDrawer}
            onNavigate={onNavigate}
          />
        )}
        {permissions.isSuperAdmin && !collapsedSections.ADMIN && (
          <NavButton
            label="System"
            path="/react/system"
            icon={<SlidersHorizontal size={18} />}
            isLink={true}
            isCollapsed={effectiveCollapsed}
            isInDrawer={isInDrawer}
            onNavigate={onNavigate}
          />
        )}

        {(permissions.isSuperAdmin || permissions.isMemberPro) && (
          <NavSection
            label="PRO"
            isCollapsed={effectiveCollapsed}
            isSectionCollapsed={collapsedSections.PRO}
            onToggle={() => toggleSection("PRO")}
          />
        )}
        {(permissions.isSuperAdmin || permissions.isMemberPro) &&
          !collapsedSections.PRO && (
            <>
              <NavButton
                label="PortfolioShield"
                path="/react/portfolio-shield/"
                activeRoute="/react/portfolio-shield/"
                icon={<ShieldCheck size={18} />}
                isLink={true}
                isCollapsed={effectiveCollapsed}
                isInDrawer={isInDrawer}
                onNavigate={onNavigate}
              />
              <NavButton
                label="Custom Entities"
                path="/react/custom-entities"
                activeRoute="/react/custom-entities"
                icon={<Fingerprint size={18} />}
                isLink={true}
                isCollapsed={effectiveCollapsed}
                isInDrawer={isInDrawer}
                onNavigate={onNavigate}
              />
              <NavButton
                label="Exchange Center"
                path="/react/exchange-center"
                activeRoute="/react/exchange-center"
                icon={<ArrowLeftRight size={18} />}
                isLink={true}
                isCollapsed={effectiveCollapsed}
                isInDrawer={isInDrawer}
                onNavigate={onNavigate}
              />
              <NavButton
                label="Ocean AI"
                path="/react/kraken"
                activeRoute="/react/kraken"
                icon={<Zap size={18} />}
                isLink={true}
                isCollapsed={effectiveCollapsed}
                isInDrawer={isInDrawer}
                onNavigate={onNavigate}
              />
            </>
          )}

        <NavSection
          label="SUPPORT"
          isCollapsed={effectiveCollapsed}
          isSectionCollapsed={collapsedSections.SUPPORT}
          onToggle={() => toggleSection("SUPPORT")}
        />

        {!collapsedSections.SUPPORT && (
          <>
            {permissions.isSuperAdmin && (
              <NavButton
                label="Help"
                path="/react/help"
                icon={<HelpCircle size={18} />}
                isLink={false}
                openModal={onFaqOpen}
                isCollapsed={effectiveCollapsed}
                isInDrawer={isInDrawer}
                onNavigate={onNavigate}
              />
            )}
            <NavButton
              label="Settings"
              path="/react/settings"
              icon={<Settings size={18} />}
              isLink={false}
              openModal={onUserSettingsOpen}
              isCollapsed={effectiveCollapsed}
              isInDrawer={isInDrawer}
              onNavigate={onNavigate}
            />
          </>
        )}
        {isInDrawer && (
          <Flex justify="center" pt="24px" pb="8px">
            <VersionBadge variant="mobile" />
          </Flex>
        )}
        <Spacer h="20px" />
      </Box>

      {/* Bottom section: Profile */}
      <Flex
        pos="absolute"
        bottom={isInDrawer ? "16px" : "13px"}
        left={isInDrawer ? "12px" : "14px"}
        right={isInDrawer ? "12px" : "14px"}
        direction="column"
        zIndex={2}
      >
        {/* Profile row */}
        <Flex
          w={effectiveCollapsed ? "32px" : "100%"}
          alignItems="center"
          justifyContent="space-between"
          gap={isInDrawer ? "10px" : undefined}
          p={isInDrawer ? "12px 14px" : undefined}
          borderRadius={isInDrawer ? "16px" : undefined}
          bg={isInDrawer ? "rgba(255, 255, 255, 0.07)" : undefined}
          border={
            isInDrawer ? "1px solid rgba(255, 255, 255, 0.10)" : undefined
          }
          boxShadow={
            isInDrawer
              ? "0 4px 14px -6px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.06)"
              : undefined
          }
          transition="background 0.2s ease-out, transform 0.15s ease-out"
          _hover={isInDrawer ? { bg: "rgba(255, 255, 255, 0.10)" } : undefined}
          _active={isInDrawer ? { transform: "scale(0.99)" } : undefined}
        >
          <Flex
            alignItems="center"
            gap={isInDrawer ? "12px" : { base: "8px", xl: "12px" }}
            flex="1"
            minW="0"
          >
            {isInDrawer ? (
              <Box
                position="relative"
                w="40px"
                h="40px"
                borderRadius="full"
                flexShrink={0}
                sx={{
                  background: user?.avatar
                    ? undefined
                    : "linear-gradient(135deg, rgba(167, 139, 250, 0.95) 0%, rgba(124, 58, 237, 0.95) 50%, rgba(91, 33, 182, 0.95) 100%)",
                  boxShadow:
                    "inset 0 0 0 1px rgba(255, 255, 255, 0.18), 0 2px 6px -2px rgba(0, 0, 0, 0.4)",
                }}
                display="flex"
                alignItems="center"
                justifyContent="center"
                overflow="hidden"
              >
                {user?.avatar ? (
                  <LazyLoadAvatar src={user.avatar} w={40} h={40} />
                ) : (
                  <Text
                    fontSize="15px"
                    fontWeight="600"
                    color="white"
                    letterSpacing="0.02em"
                    lineHeight="1"
                  >
                    {[user?.first_name?.[0], user?.last_name?.[0]]
                      .filter(Boolean)
                      .join("")
                      .toUpperCase() || "U"}
                  </Text>
                )}
              </Box>
            ) : (
              <LazyLoadAvatar src={user?.avatar ?? ""} w={32} h={32} />
            )}
            {isInDrawer ? (
              <Flex direction="column" flex="1" minW="0" gap="2px">
                <Text
                  fontSize="14px"
                  fontWeight="600"
                  color="white"
                  noOfLines={1}
                  lineHeight="1.25"
                  letterSpacing="-0.01em"
                >
                  {[user?.first_name, user?.last_name]
                    .filter(Boolean)
                    .join(" ") || user?.first_name}
                </Text>
                {user?.email && (
                  <Text
                    fontSize="11.5px"
                    color="rgba(255, 255, 255, 0.5)"
                    noOfLines={1}
                    lineHeight="1.3"
                    fontWeight="400"
                  >
                    {user.email}
                  </Text>
                )}
              </Flex>
            ) : (
              <Text
                textStyle={{ base: "b-xs", xl: "b-sm" }}
                fontWeight="500"
                color="white"
                w={effectiveCollapsed ? "0" : { base: "80px", xl: "120px" }}
                noOfLines={1}
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                transition="0.2s ease-out"
              >
                {user?.first_name}
              </Text>
            )}
          </Flex>
          {!effectiveCollapsed && (
            <Menu placement="top-end">
              <MenuButton
                as={IconButton}
                w={isInDrawer ? "36px" : { base: "20px", xl: "40px" }}
                h={isInDrawer ? "36px" : undefined}
                minW={isInDrawer ? "36px" : undefined}
                borderRadius={isInDrawer ? "10px" : undefined}
                bg="transparent"
                color="rgba(255, 255, 255, 0.7)"
                _hover={{ bg: "rgba(255, 255, 255, 0.10)", color: "white" }}
                _active={{ bg: "rgba(255, 255, 255, 0.16)" }}
                transition="0.2s ease-out"
                aria-label="profile settings"
                variant="unstyled"
                size="sm"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                icon={<MoreVertical size={18} />}
              />
              <MenuList minWidth={{ base: "180px", xl: "200px" }}>
                <MenuItem
                  onClick={() => (isNative ? logoutNative() : logout())}
                  icon={<LogOut size={18} />}
                >
                  Logout
                </MenuItem>
              </MenuList>
            </Menu>
          )}
        </Flex>
      </Flex>
    </>
  );
};

const BottomTabBar = ({
  permissions,
  onMoreClick,
  isMoreOpen,
}: {
  permissions: ReturnType<typeof useAuth>["permissions"];
  onMoreClick: () => void;
  isMoreOpen: boolean;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const couldUseNative = isNativeTabBarAvailable();
  const [nativeFailed, setNativeFailed] = useState(false);
  const useNative = couldUseNative && !nativeFailed;
  const isHidden = useTabBarStore((s) => s.isHidden);

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log(
      "[NativeTabBar] platform=",
      Capacitor.getPlatform(),
      "useNative=",
      useNative,
      "failed=",
      nativeFailed,
    );
  }, [useNative, nativeFailed]);
  // Note: the bottom bar intentionally uses brand indigo (matching the "+"
  // FAB) across all roles, including super-admins. The Pro violet theme is
  // reserved for the side sidebar only — the bottom bar should stay
  // consistent with the global primary action color.

  type LinkTab = {
    kind: "link";
    key: string;
    label: string;
    path: string;
    icon: React.ElementType;
    activeRoute?: string;
  };
  type ActionTab = {
    kind: "action";
    key: string;
    label: string;
    icon: React.ElementType;
    onClick: () => void;
  };
  type Tab = LinkTab | ActionTab;

  const firstTab: LinkTab = permissions.isAdmin
    ? {
        kind: "link",
        key: "tickets",
        label: "Tickets",
        path: "/react/tickets",
        icon: Ticket,
        activeRoute: "/react/tickets",
      }
    : permissions.isMember
      ? {
          kind: "link",
          key: "review",
          label: "Review",
          path: "/member/review/platform_items",
          icon: SearchCheck,
          activeRoute: "/member/review",
        }
      : {
          kind: "link",
          key: "dashboard",
          label: "Home",
          path: "/react/dashboard",
          icon: LayoutGrid,
          activeRoute: "/react/dashboard",
        };

  const linkTabs: LinkTab[] = [firstTab];

  if (firstTab.key !== "dashboard") {
    linkTabs.push({
      kind: "link",
      key: "dashboard-2",
      label: "Dashboard",
      path: "/react/dashboard",
      icon: LayoutGrid,
      activeRoute: "/react/dashboard",
    });
  }

  linkTabs.push(
    {
      kind: "link",
      key: "items",
      label: "Items",
      path: "/react/items/overview",
      icon: Package,
      activeRoute: "/react/items",
    },
    {
      kind: "link",
      key: "reporting",
      label: "Report",
      path: "/react/reporting",
      icon: BarChart3,
      activeRoute: "/react/reporting",
    },
  );

  const tabs: Tab[] = [
    ...linkTabs,
    {
      kind: "action",
      key: "more",
      label: "More",
      icon: MoreHorizontal,
      onClick: onMoreClick,
    },
  ];

  // Apple Arcade–style bar: dark translucent floating pill with an active
  // chip (primary.400 indigo) behind the selected tab. Inactive tabs show
  // icon only; active shows icon + label side-by-side.
  const reducedMotion = useReducedMotion();
  const barTabs = tabs.filter((t) => t.kind === "link") as LinkTab[];
  const moreTab = tabs.find((t) => t.kind === "action") as
    | ActionTab
    | undefined;
  const activeKey =
    barTabs.find((t) => location.pathname.startsWith(t.activeRoute ?? t.path))
      ?.key ?? null;

  const sfSymbolFor = (key: string): string => {
    switch (key) {
      case "tickets":
        return "ticket";
      case "review":
        return "checkmark.seal";
      case "dashboard":
      case "dashboard-2":
        return "square.grid.2x2";
      case "items":
        return "shippingbox";
      case "reporting":
        return "chart.bar";
      case "more":
        return "ellipsis";
      default:
        return "circle";
    }
  };

  const nativeItems: NativeTabItem[] = [
    ...barTabs.map((t) => ({
      key: t.key,
      label: t.label,
      sfSymbol: sfSymbolFor(t.key),
      kind: "link" as const,
    })),
    ...(moreTab
      ? [
          {
            key: moreTab.key,
            label: moreTab.label,
            sfSymbol: sfSymbolFor(moreTab.key),
            kind: "action" as const,
          },
        ]
      : []),
  ];

  const linkPathByKey = React.useMemo(() => {
    const map: Record<string, string> = {};
    barTabs.forEach((t) => {
      map[t.key] = t.path;
    });
    return map;
  }, [barTabs]);

  useEffect(() => {
    if (!couldUseNative || nativeFailed) return;
    let cancelled = false;
    const handles: Array<{ remove: () => void | Promise<void> }> = [];

    (async () => {
      const tabHandle = await nativeTabBar.addListener(
        "tabSelected",
        ({ key }) => {
          const path = linkPathByKey[key];
          if (path) navigate(path);
        },
      );
      if (cancelled) {
        tabHandle.remove();
        return;
      }
      handles.push(tabHandle);

      const moreHandle = await nativeTabBar.addListener("moreSelected", () => {
        onMoreClick();
      });
      if (cancelled) {
        moreHandle.remove();
        return;
      }
      handles.push(moreHandle);
    })();

    return () => {
      cancelled = true;
      handles.forEach((h) => h.remove());
      nativeTabBar.hide().catch(() => {});
    };
    // Only wire listeners once per mount; config updates go through separate effects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couldUseNative, nativeFailed]);

  useEffect(() => {
    if (!couldUseNative || nativeFailed) return;
    if (isHidden) {
      nativeTabBar.hide().catch(() => {});
      return;
    }
    nativeTabBar
      .show({ tabs: nativeItems, activeKey: activeKey ?? undefined })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn(
          "[NativeTabBar] show() failed, falling back to web bar:",
          err,
        );
        setNativeFailed(true);
      });
    // nativeItems/activeKey are re-derived each render; depending on isHidden alone
    // avoids thrashing show() on every render while still reacting to tab config.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couldUseNative, nativeFailed, isHidden]);

  useEffect(() => {
    if (!useNative || !activeKey || isHidden) return;
    nativeTabBar.setActive({ key: activeKey }).catch(() => {});
  }, [useNative, activeKey, isHidden]);

  if (useNative) return null;

  const chipSpring = reducedMotion
    ? { duration: 0 }
    : { type: "spring" as const, stiffness: 420, damping: 38 };

  const renderTab = (tab: LinkTab) => {
    const isActive = activeKey === tab.key;
    // Light glass bar → dark inactive text, brand indigo active (bold for legibility over the glass chip)
    const iconColor = isActive ? "primary.500" : "rgba(0,0,0,0.65)";
    const labelColor = isActive ? "primary.500" : "rgba(0,0,0,0.65)";

    return (
      <Link
        key={tab.key}
        to={tab.path}
        draggable={false}
        onContextMenu={(e) => e.preventDefault()}
        style={{
          flex: 1,
          display: "flex",
          position: "relative",
          zIndex: 2,
          WebkitTapHighlightColor: "transparent",
          WebkitTouchCallout: "none",
          WebkitUserSelect: "none",
          userSelect: "none",
        }}
      >
        <Flex
          flex="1"
          direction="column"
          align="center"
          justify="center"
          gap="3px"
          minH="44px"
          cursor="pointer"
          position="relative"
          _active={{ transform: "scale(0.96)" }}
          transition="transform 140ms cubic-bezier(0.2, 0.8, 0.2, 1)"
          sx={{
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          }}
          aria-label={tab.label}
          aria-current={isActive ? "page" : undefined}
        >
          {/* Morphing active chip — Glass Button technique adapted from
              the reference CSS: layered inset shadows create rim light +
              top gloss + bottom shadow, conic outline via ::after. */}
          {isActive && (
            <Box
              as={motion.div}
              layoutId="bottom-tab-active-chip"
              transition={chipSpring}
              position="absolute"
              top="3px"
              bottom="3px"
              left="-6px"
              right="-6px"
              borderRadius="9999px"
              pointerEvents="none"
              sx={{
                // Exact liquid-glass CSS from the reference card:
                // transparent body, isolation isolate, outer glow, and two
                // pseudo-layers (::before = tint+inner rim, ::after = SVG
                // refraction). Icons/labels sit above via parent zIndex.
                isolation: "isolate",
                zIndex: 0,
                boxShadow: "0 0 21px -8px rgba(0,0,0,0.3)",
                // Tint + inner shadow (per reference .liquid-glass-card::before)
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  borderRadius: "inherit",
                  boxShadow: "inset 0 0 6px -1px rgba(255,255,255,0.7)",
                  background: "rgba(255,255,255,0.02)",
                  pointerEvents: "none",
                  zIndex: 0,
                },
                // SVG distortion layer (per reference .liquid-glass-card::after)
                "&::after": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  borderRadius: "inherit",
                  filter: "url(#liquid-glass-distortion)",
                  WebkitFilter: "url(#liquid-glass-distortion)",
                  pointerEvents: "none",
                  zIndex: -1,
                },
              }}
            />
          )}

          <Box
            color={iconColor}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            transition="color 180ms ease-out"
          >
            <tab.icon size={20} strokeWidth={isActive ? 2.4 : 1.9} />
          </Box>

          <AnimatePresence mode="popLayout" initial={false}>
            {isActive && (
              <motion.div
                key="label"
                initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -2 }}
                animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
                exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -2 }}
                transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
              >
                <Text
                  fontSize="10px"
                  fontWeight="600"
                  color={labelColor}
                  lineHeight="1"
                  whiteSpace="nowrap"
                  px="2px"
                  transition="color 180ms ease-out"
                >
                  {tab.label}
                </Text>
              </motion.div>
            )}
          </AnimatePresence>
        </Flex>
      </Link>
    );
  };

  const moreActive = moreTab ? isMoreOpen : false;
  const moreIconColor = moreActive ? "primary.400" : "rgba(0,0,0,0.55)";

  return (
    <Box
      display={{ base: "block", md: "none" }}
      position="fixed"
      bottom="0"
      left="0"
      right="0"
      zIndex={30}
      pb="env(safe-area-inset-bottom)"
      px="12px"
      pt="8px"
      pointerEvents="none"
    >
      {/* SVG displacement filter — defined once, applied via filter: url(#id)
          on the pseudo-element backdrop. Works on Safari/iOS (unlike using
          the filter via backdrop-filter). */}
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden>
        <defs>
          <filter
            id="liquid-glass-distortion"
            x="0%"
            y="0%"
            width="100%"
            height="100%"
          >
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.001 0.001"
              numOctaves={2}
              seed={92}
              result="noise"
            />
            <feGaussianBlur in="noise" stdDeviation={2} result="blurred" />
            <feDisplacementMap
              in="SourceGraphic"
              in2="blurred"
              scale={150}
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </defs>
      </svg>

      <AnimatePresence initial={false}>
        {!isHidden && (
          <motion.div
            key="bottom-tab-bar"
            initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 24 }}
            animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
            exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 24 }}
            transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
            style={{ pointerEvents: "auto" }}
          >
            <Flex align="center" gap="8px" pointerEvents="auto">
              {/* Main floating pill — simple translucent white.
            The liquid-glass element is the ACTIVE CHIP, not the bar. */}
              <Flex
                flex="1"
                position="relative"
                h="56px"
                borderRadius="9999px"
                align="stretch"
                px="6px"
                sx={{
                  background: "rgba(255,255,255,0.72)",
                  backdropFilter: "blur(20px) saturate(180%)",
                  WebkitBackdropFilter: "blur(20px) saturate(180%)",
                  boxShadow: [
                    "0 8px 24px -8px rgba(0,0,0,0.2)",
                    "inset 0 0 0 0.5px rgba(255,255,255,0.8)",
                  ].join(", "),
                }}
              >
                {barTabs.map(renderTab)}
              </Flex>

              {/* Detached right action pill — same liquid glass */}
              {moreTab && (
                <Box
                  as="button"
                  onClick={moreTab.onClick}
                  aria-label={moreTab.label}
                  w="56px"
                  h="56px"
                  borderRadius="9999px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  color={moreIconColor}
                  cursor="pointer"
                  flexShrink={0}
                  position="relative"
                  transition="color 180ms ease-out, transform 140ms cubic-bezier(0.2, 0.8, 0.2, 1)"
                  _active={{ transform: "scale(0.94)" }}
                  sx={{
                    background: "rgba(255,255,255,0.72)",
                    backdropFilter: "blur(20px) saturate(180%)",
                    WebkitBackdropFilter: "blur(20px) saturate(180%)",
                    border: "none",
                    WebkitTapHighlightColor: "transparent",
                    boxShadow: [
                      "0 8px 24px -8px rgba(0,0,0,0.2)",
                      "inset 0 0 0 0.5px rgba(255,255,255,0.8)",
                    ].join(", "),
                    "@media (prefers-reduced-motion: reduce)": {
                      transition: "none",
                    },
                  }}
                >
                  <Box as="span" position="relative" zIndex={1} display="flex">
                    <moreTab.icon
                      size={22}
                      strokeWidth={moreActive ? 2.4 : 1.9}
                    />
                  </Box>
                </Box>
              )}
            </Flex>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

const ReactAdminWrapper = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState("");
  const isTabBarHidden = useTabBarStore((s) => s.isHidden);

  // Paths where header should be hidden
  const hideHeaderPaths = ["/react/reporting/create", "/member/brand/"];
  const shouldHideHeader = hideHeaderPaths.some((path) =>
    location.pathname.startsWith(path),
  );
  // Mobile-only: hide the header on seizure detail routes (keep desktop layout untouched)
  const isSeizureDetailRoute =
    /^\/react\/custom-entities\/seizures\/[^/]+\/?$/.test(location.pathname);
  // Mobile-only: hide the header on task add/edit routes (in-page header replaces it)
  const isTaskAddEditRoute =
    /^\/react\/pipeline\/(add-task|edit-task\/[^/]+)\/?$/.test(
      location.pathname,
    );
  // Mobile-only: hide the header on seller detail route (in-page header replaces it).
  // Match /react/sellers/<id> but NOT /react/sellers/list/*, /react/sellers/add, /react/sellers/edit/<id>
  const isSellerDetailRoute =
    /^\/react\/sellers\/(?!list|add|edit)([^/]+)\/?$/.test(location.pathname);
  // Mobile-only: hide the header on SAW domain detail route (in-page header replaces it)
  const isSawDetailRoute = /^\/react\/saw\/sites\/[^/]+\/?$/.test(
    location.pathname,
  );
  // Mobile-only: hide the header on seller add/edit routes (in-page header replaces it)
  const isSellerAddEditRoute = /^\/react\/sellers\/(add|edit\/[^/]+)\/?$/.test(
    location.pathname,
  );
  // Mobile-only: hide the header on member detail route (in-page header replaces it).
  // Match /react/members/<id> but NOT /react/members/list, /react/members/add, /react/members/edit/<id>, /react/members/<id>/brand/*
  const isMemberDetailRoute =
    /^\/react\/members\/(?!list|add|edit)([^/]+)\/?$/.test(location.pathname);
  // Mobile-only: hide the header on member add/edit routes (in-page header replaces it)
  const isMemberAddEditRoute = /^\/react\/members\/(add|edit\/[^/]+)\/?$/.test(
    location.pathname,
  );

  // Mobile-only: hide the header on brand add/edit routes (in-page header replaces it)
  const isBrandAddEditRoute =
    /^\/react\/members\/[^/]+\/brand\/(add|edit\/[^/]+)\/?$/.test(
      location.pathname,
    );
  // Mobile-only: hide the header on manual item add route (in-page header replaces it)
  const isItemAddManualRoute = /^\/react\/items\/add-manual\/?$/.test(
    location.pathname,
  );
  const hideHeaderOnMobile =
    isSeizureDetailRoute ||
    isTaskAddEditRoute ||
    isSellerDetailRoute ||
    isSawDetailRoute ||
    isSellerAddEditRoute ||
    isMemberDetailRoute ||
    isMemberAddEditRoute ||
    isBrandAddEditRoute ||
    isItemAddManualRoute;

  // Paths where horizontal padding should be removed (e.g. for drag-to-select)
  const noHorizontalPaddingPaths = ["/react/reporting"];
  const shouldRemoveHorizontalPadding = noHorizontalPaddingPaths.some(
    (path) => location.pathname === path || location.pathname === path + "/",
  );

  const NAVIGATION_DATA = [
    {
      label: "Notifications",
      path: "/react/notifications",
    },
    {
      label: "Tickets",
      path: "/react/tickets",
    } /*
  {
    label: 'Onboarding',
    path: '/react/onboarding',
  },*/,
    {
      label: "Review",
      path: "/member/review",
    },
    {
      label: "My Organization",
      path: "/member/organization",
    },
    {
      label: "Dashboard",
      path: "/react/dashboard",
    },
    {
      label: "Reporting",
      path: "/react/reporting",
    },
    {
      label: "Sites",
      path: "/react/sites",
    },
    {
      label: "Platform Items",
      path: "/react/items/list",
    },
    {
      label: "Ocean AI",
      path: "/react/kraken",
    },
    {
      label: "Platform Items",
      path: "/react/items/overview",
    },
    {
      label: "Items",
      path: "/react/items/",
    },
    {
      label: "Whitelisting",
      path: "/react/whitelist",
    },
    {
      label: "Pipeline",
      path: "/react/pipeline",
    },
    {
      label: "Reviewing",
      path: "/react/pipeline/4",
    },
    {
      label: "Intelligence",
      path: "/react/intelligence",
    },
    {
      label: "AI Classifier",
      path: "/react/ai",
    },
    {
      label: "Platforms",
      path: "/react/platforms",
    },
    {
      label: "Standalone Sites",
      path: "/react/saw",
    },
    {
      label: "Sellers",
      path: "/react/sellers",
    },
    {
      label: "Members",
      path: "/react/members",
    },
    {
      label: "Hour Registrations",
      path: "/react/hour-registrations",
    },
    {
      label: "Users",
      path: "/react/users",
    },
    {
      label: "System",
      path: "/react/system",
    },
    {
      label: "Help",
      path: "/react/help",
    },
    {
      label: "Settings",
      path: "/react/settings",
    },
    {
      label: "Portfolio Shield",
      path: "/react/portfolio-shield",
    },
    {
      label: "Custom Entities",
      path: "/react/custom-entities",
    },
    {
      label: "Exchange Center",
      path: "/react/exchange-center",
    },
  ];

  const [secondary400, secondary500, secondary700] = useToken("colors", [
    "secondary.400",
    "secondary.500",
    "secondary.700",
  ]);

  const bgColor =
    import.meta.env.MODE === "dev"
      ? secondary400
      : import.meta.env.MODE === "staging"
        ? secondary500
        : secondary700;

  const [navCollapsed, setNavCollapsed] = useState(getSidebarCollapsed);
  const [isAppNavOpen, setAppNavOpen] = useState(
    () => !getAppSideNavCollapsed(),
  );
  const [collapsedSections, setCollapsedSections] = useState<{
    [key: string]: boolean;
  }>({
    HOME: false,
    WORK: false,
    ENTITIES: false,
    ADMIN: false,
    PRO: false,
    SUPPORT: false,
  });

  const { logout, logoutNative, user, permissions } = useAuth();
  const isNative = Capacitor.isNativePlatform();

  // Ocean Pro - always on for super admins
  const isProMode = permissions.isSuperAdmin;

  const {
    isOpen: isUserSettingsOpen,
    onOpen: onUserSettingsOpen,
    onClose: onUserSettingsClose,
  } = useDisclosure();

  const {
    isOpen: isPluginOpen,
    onOpen: _,
    onClose: onPluginClose,
  } = useDisclosure();

  const {
    isOpen: isFaqOpen,
    onOpen: onFaqOpen,
    onClose: onFaqClose,
  } = useDisclosure();

  const {
    isOpen: isNotificationDrawerOpen,
    onOpen: onNotificationDrawerOpen,
    onClose: onNotificationDrawerClose,
  } = useDisclosure();

  const {
    isOpen: isMobileNavOpen,
    onOpen: onMobileNavOpen,
    onClose: onMobileNavClose,
  } = useDisclosure();

  useHideTabBar(isMobileNavOpen, "mobile-nav");

  const isMobile = useBreakpointValue({ base: true, md: false });

  const reviewStatus = useMemberReviewStatus(permissions.isMember);

  const hasPendingReviews =
    reviewStatus.data?.platform_items ||
    reviewStatus.data?.saws ||
    reviewStatus.data?.site_items;

  useEffect(() => {
    const currentNavButton = NAVIGATION_DATA.find(
      (button: { label: string; path: string }) =>
        new RegExp(`${button.path}*`).test(location.pathname),
    );

    if (currentNavButton) {
      setCurrentPage(currentNavButton.label);
    } else {
      setCurrentPage("");
    }
  }, [location.pathname]);

  useEffect(() => {
    setSidebarCollapsed(navCollapsed);
  }, [navCollapsed]);

  useEffect(() => {
    setAppSideNavCollapsed(!isAppNavOpen);
  }, [isAppNavOpen]);

  useEffect(() => {
    onMobileNavClose();
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobile) onMobileNavClose();
  }, [isMobile]);

  const toggleSection = (sectionName: string) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionName]: !prev[sectionName],
    }));
  };

  return (
    <KrakenProvider>
      <NewVersionModal />
      <ProfileModal isOpen={isUserSettingsOpen} onClose={onUserSettingsClose} />
      <FaqModal isOpen={isFaqOpen} onClose={onFaqClose} />
      <PluginDrawer isOpen={isPluginOpen} onClose={onPluginClose} />

      {/* Mobile-only fixed wallpaper — light grey top fading to a soft purple glow at bottom */}
      <Box
        display={{ base: "block", md: "none" }}
        position="fixed"
        top="0"
        left="0"
        right="0"
        bottom="0"
        zIndex={0}
        pointerEvents="none"
        sx={{
          background: [
            "radial-gradient(800px 400px at 50% 115%, rgba(54,46,173,0.16), transparent 65%)",
            "linear-gradient(180deg, #EEEFF3 0%, #EEEFF3 50%, #DCD9EC 100%)",
          ].join(", "),
        }}
      />

      <Flex
        h={{ base: "auto", md: "100vh" }}
        minH={{ base: "100dvh", md: undefined }}
        w="100%"
        maxW="100vw"
        overflowY={{ base: "visible", md: "hidden" }}
        overflowX="clip"
        p={{ base: "0", md: "8px" }}
        gap={{ base: "0", md: "8px" }}
        position="relative"
        zIndex={1}
        sx={{
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {/* Mobile drawer (G) */}
        <Drawer
          isOpen={isMobileNavOpen}
          placement="left"
          onClose={onMobileNavClose}
          size="xs"
        >
          <DrawerOverlay
            bg="rgba(10, 10, 20, 0.55)"
            sx={{ backdropFilter: "blur(4px)" }}
          />
          <DrawerContent
            bg={isProMode ? "transparent" : bgColor}
            maxW="86vw"
            borderTopRightRadius="20px"
            borderBottomRightRadius="20px"
            boxShadow="8px 0 32px rgba(0, 0, 0, 0.35)"
            sx={{
              paddingTop: "env(safe-area-inset-top)",
              paddingBottom: "env(safe-area-inset-bottom)",
            }}
          >
            <OceanProSidebarOverlay isActive={isProMode} />
            <DrawerBody p="0" position="relative" zIndex={1}>
              <SidebarContent
                navCollapsed={false}
                setNavCollapsed={() => {}}
                isInDrawer={true}
                onNavigate={onMobileNavClose}
                permissions={permissions}
                user={user}
                isNative={isNative}
                logout={logout}
                logoutNative={logoutNative}
                hasPendingReviews={hasPendingReviews}
                collapsedSections={collapsedSections}
                toggleSection={toggleSection}
                onFaqOpen={onFaqOpen}
                onUserSettingsOpen={onUserSettingsOpen}
                bgColor={bgColor}
                isProMode={isProMode}
                isNotificationDrawerOpen={isNotificationDrawerOpen}
                onNotificationDrawerOpen={onNotificationDrawerOpen}
                onNotificationDrawerClose={onNotificationDrawerClose}
              />
            </DrawerBody>
          </DrawerContent>
        </Drawer>

        {/* Desktop app sidebar + nav */}
        <Flex display={{ base: "none", md: "flex" }} flexShrink={0} gap="8px">
          <AppSideNav
            isOpen={isAppNavOpen}
            onToggle={() => setAppNavOpen(!isAppNavOpen)}
            isReact={permissions.isReact}
          />

          <Box
            position="relative"
            w={navCollapsed ? "66px" : { base: "185px", xl: "220px" }}
            minW={navCollapsed ? "66px" : { base: "185px", xl: "220px" }}
            h="calc(100vh - 16px)"
            bg={isProMode ? "transparent" : bgColor}
            borderRadius="8px"
            p="13px 19px"
            transition="0.2s ease-out"
            overflow="hidden"
          >
            <OceanProSidebarOverlay isActive={isProMode} />
            <SidebarContent
              navCollapsed={navCollapsed}
              setNavCollapsed={setNavCollapsed}
              isInDrawer={false}
              permissions={permissions}
              user={user}
              isNative={isNative}
              logout={logout}
              logoutNative={logoutNative}
              hasPendingReviews={hasPendingReviews}
              collapsedSections={collapsedSections}
              toggleSection={toggleSection}
              onFaqOpen={onFaqOpen}
              onUserSettingsOpen={onUserSettingsOpen}
              bgColor={bgColor}
              isProMode={isProMode}
              isNotificationDrawerOpen={isNotificationDrawerOpen}
              onNotificationDrawerOpen={onNotificationDrawerOpen}
              onNotificationDrawerClose={onNotificationDrawerClose}
            />
          </Box>
        </Flex>

        <Box
          id="main-content-area"
          flex={{ base: "1 1 0", md: "initial" }}
          minW={0}
          maxW="100%"
          w={{
            base: "100%",
            md: navCollapsed ? "calc(100vw - 66px)" : "calc(100vw - 193px)",
            xl: navCollapsed ? "calc(100vw - 66px)" : "calc(100vw - 244px)",
          }}
          h={{ base: "auto", md: "calc(100vh - 16px)" }}
          minH={{ base: "100dvh", md: "auto" }}
          transition="0.2s ease-out"
          pt={{ base: "max(env(safe-area-inset-top), 8px)", md: "10px" }}
          pb={{
            base: isTabBarHidden
              ? "env(safe-area-inset-bottom)"
              : "calc(82px + env(safe-area-inset-bottom))",
            md: "0",
          }}
          pl={{
            base:
              shouldHideHeader || shouldRemoveHorizontalPadding ? "0" : "14px",
            md:
              shouldHideHeader || shouldRemoveHorizontalPadding ? "0" : "20px",
          }}
          pr={{
            base:
              shouldHideHeader || shouldRemoveHorizontalPadding ? "0" : "14px",
            md:
              shouldHideHeader || shouldRemoveHorizontalPadding ? "0" : "20px",
          }}
          overflowX="clip"
          overflowY={{
            base: "visible",
            md: shouldHideHeader ? "hidden" : "auto",
          }}
        >
          {!shouldHideHeader && (
            <Flex
              w="100%"
              px={shouldRemoveHorizontalPadding ? "20px" : undefined}
              alignItems="center"
              mb={{ base: "12px", md: "16px" }}
              mt={{ base: "2px", md: 0 }}
              display={
                hideHeaderOnMobile ? { base: "none", md: "flex" } : "flex"
              }
            >
              {currentPage === "" ? (
                <Button
                  variant="neutral"
                  leftIcon={<ChevronLeft size={16} />}
                  onClick={() => navigate(-1)}
                >
                  Back
                </Button>
              ) : (
                <Text
                  fontSize={{ base: "26px", md: "24px" }}
                  fontWeight={{ base: "700", md: "inherit" }}
                  color="neutral.900"
                  letterSpacing={{ base: "-0.03em", md: "normal" }}
                  lineHeight={{ base: "1.05", md: "inherit" }}
                  textStyle={{ base: undefined, md: "h4" }}
                  sx={{
                    fontFeatureSettings: '"ss01", "cv11"',
                  }}
                >
                  {currentPage}
                </Text>
              )}

              <Spacer />

              <Box display={{ base: "flex", md: "none" }} position="relative">
                <IconButton
                  aria-label="Notifications"
                  icon={<BellDot size={18} strokeWidth={1.75} />}
                  onClick={onNotificationDrawerOpen}
                  w="42px"
                  h="42px"
                  minW="42px"
                  borderRadius="12px"
                  bg={isProMode ? "rgba(30, 27, 75, 1)" : "secondary.700"}
                  color="white"
                  variant="solid"
                  boxShadow="0 4px 12px -4px rgba(54, 46, 173, 0.45)"
                  transition="transform 120ms ease-out, box-shadow 120ms ease-out"
                  _hover={{
                    bg: isProMode ? "rgba(30, 27, 75, 1)" : "secondary.700",
                    boxShadow: "0 6px 16px -4px rgba(54, 46, 173, 0.55)",
                  }}
                  _active={{
                    transform: "scale(0.94)",
                    boxShadow: "0 2px 8px -2px rgba(54, 46, 173, 0.35)",
                  }}
                  sx={{
                    "@media (prefers-reduced-motion: reduce)": {
                      transition: "none",
                    },
                  }}
                />
                <Box display="none">
                  <NotificationBellButton
                    isOpen={isNotificationDrawerOpen}
                    onOpen={onNotificationDrawerOpen}
                    onClose={onNotificationDrawerClose}
                  />
                </Box>
              </Box>

              <Flex
                display={{ base: "none", md: "flex" }}
                alignItems="center"
                gap="12px"
              >
                <VersionBadge />
                <NotificationBellButton
                  isOpen={isNotificationDrawerOpen}
                  onOpen={onNotificationDrawerOpen}
                  onClose={onNotificationDrawerClose}
                />
                {permissions.isReact && <ClockInClockOut />}
              </Flex>
            </Flex>
          )}
          <Box w="100%" maxW="100%" pb="20px" overflowX="clip">
            {children}
          </Box>
        </Box>
        <BottomTabBar
          permissions={permissions}
          onMoreClick={onMobileNavOpen}
          isMoreOpen={isMobileNavOpen}
        />
      </Flex>
      <FloatingNotifications
        isDrawerOpen={isNotificationDrawerOpen}
        onOpen={onNotificationDrawerOpen}
      />

      {/* Global Ocean AI FAB */}
      {(permissions.isSuperAdmin || permissions.isMemberPro) && (
        <KrakenGlobalFab />
      )}
    </KrakenProvider>
  );
};

export default ReactAdminWrapper;
